import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Textarea } from '~/components/ui/textarea';
import ClassroomDialog from './ClassroomDialog';
import ClassOfferingDialog from './ClassOfferingDialog';
import { WEEKDAYS, WEEKDAY_LABELS, type Weekday } from './scheduleUtils';
import { useCreateClassScheduleSeries, useCreateScheduleBlock, useUpdateScheduleBlock } from './useScheduleBlocks';

const NO_VALUE = '__none__';

const ACTIVITY_KINDS = [
  ['CLASS', 'Class'],
  ['CHAPEL', 'Chapel'],
  ['RECESS', 'Recess'],
  ['ASSEMBLY', 'Assembly'],
  ['LUNCH', 'Lunch'],
  ['PICKUP', 'Pickup'],
  ['DROPOFF', 'Drop-off'],
  ['STAFF_MEETING', 'Staff meeting'],
  ['OTHER', 'Other'],
] as const;

type ActivityKind = (typeof ACTIVITY_KINDS)[number][0];
type Recurrence = 'ONE_TIME' | 'WEEKLY';

interface PersonOption {
  id: string;
  firstName: string;
  lastName: string;
}

export interface StaffOption {
  id: string;
  kind: string;
  state: string;
  person: PersonOption;
}

export interface StudentOption {
  person: PersonOption;
  gradeLevel?: string | null;
}

export interface ClassOption {
  id: string;
  displayName: string;
  term: TermOption;
  courseId?: string;
  courseName?: string;
  section?: string;
}

export interface CourseOption {
  id: string;
  name: string;
}

export interface TermOption {
  id: string;
  name: string;
  startsOn?: string | null;
  endsOn?: string | null;
}

export function termOption(term: {
  id: string;
  name: string;
  startsOn?: unknown;
  endsOn?: unknown;
}): TermOption {
  return {
    id: term.id,
    name: term.name,
    startsOn: term.startsOn == null ? null : String(term.startsOn),
    endsOn: term.endsOn == null ? null : String(term.endsOn),
  };
}

export interface ClassroomOption {
  id: string;
  name: string;
}

export interface ScheduleBlockValue {
  id: string;
  title?: string | null;
  displayTitle: string;
  activityKind: string;
  startsAt: string;
  endsAt: string;
  timeLabel: string;
  weekdays: ReadonlyArray<string>;
  oneTime: boolean;
  oneTimeDate?: string | null;
  attendanceEnabled: boolean;
  notes?: string | null;
  term?: TermOption | null;
  classroom?: ClassroomOption | null;
  audiences: ReadonlyArray<{
    kind: string;
    gradeLevelKey?: string | null;
    classOffering?: { id: string; displayName: string; term: TermOption } | null;
    person?: PersonOption | null;
  }>;
  staffAssignments: ReadonlyArray<{
    roleLabel: string;
    canTakeAttendance: boolean;
    staffMember: StaffOption;
  }>;
}

interface SelectedStaff {
  staffMemberId: string;
  roleLabel: string;
  canTakeAttendance: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYearId: string;
  schoolId: string;
  schoolYearStart: string;
  schoolYearEnd: string;
  defaultDate: string;
  defaultStartsAt?: string;
  defaultEndsAt?: string;
  defaultClassOfferingId?: string;
  classrooms: ReadonlyArray<ClassroomOption>;
  staff: ReadonlyArray<StaffOption>;
  students: ReadonlyArray<StudentOption>;
  classes: ReadonlyArray<ClassOption>;
  courses?: ReadonlyArray<CourseOption>;
  terms: ReadonlyArray<TermOption>;
  gradeLevels: ReadonlyArray<string>;
  block?: ScheduleBlockValue | null;
  onComplete: () => void | Promise<void>;
  onRefreshError?: (message: string) => void;
  onLocationCreated?: () => void | Promise<void>;
  onClassOptionsRefresh?: () => void | Promise<void>;
  configureTermsHref?: string;
  allowInlineClassCreation?: boolean;
}

function displayName(person: PersonOption) {
  return `${person.firstName} ${person.lastName}`.trim();
}

function toggleValue<T extends string>(values: T[], value: T, checked: boolean) {
  return checked ? [...new Set([...values, value])] : values.filter((item) => item !== value);
}

function weekdayForDate(value: string): Weekday {
  const [year, month, day] = value.split('-').map(Number);
  return WEEKDAYS[new Date(year, month - 1, day).getDay()];
}

function plusOneHour(value: string) {
  const [hour, minute] = value.split(':').map(Number);
  return `${String(Math.min(hour + 1, 23)).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function dateBounds(schoolYearStart: string, schoolYearEnd: string, term?: TermOption | null) {
  const startsOn = term?.startsOn && term.startsOn > schoolYearStart ? term.startsOn : schoolYearStart;
  const endsOn = term?.endsOn && term.endsOn < schoolYearEnd ? term.endsOn : schoolYearEnd;
  return startsOn <= endsOn ? { startsOn, endsOn } : { startsOn: schoolYearStart, endsOn: schoolYearEnd };
}

function clampDate(value: string, startsOn: string, endsOn: string) {
  if (value < startsOn) return startsOn;
  if (value > endsOn) return endsOn;
  return value;
}

function matchingClassTermOptions(classes: ReadonlyArray<ClassOption>, selected?: ClassOption) {
  if (!selected) return [];
  if (!selected.courseId || !selected.section) return [selected];

  return classes.filter((item) => item.courseId === selected.courseId && item.section === selected.section);
}

export default function ScheduleBlockDialog({
  open,
  onOpenChange,
  schoolYearId,
  schoolId,
  schoolYearStart,
  schoolYearEnd,
  defaultDate,
  defaultStartsAt = '08:00',
  defaultEndsAt,
  defaultClassOfferingId,
  classrooms,
  staff,
  students,
  classes,
  courses = [],
  terms,
  gradeLevels,
  block,
  onComplete,
  onRefreshError,
  onLocationCreated,
  onClassOptionsRefresh,
  configureTermsHref,
  allowInlineClassCreation = false,
}: Props) {
  const openSession = useRef(false);
  const sessionGeneration = useRef(0);
  const sessionIdentity = useRef({ open: false, schoolYearId: '' });
  if (
    sessionIdentity.current.open !== open ||
    sessionIdentity.current.schoolYearId !== schoolYearId
  ) {
    sessionIdentity.current = { open, schoolYearId };
    sessionGeneration.current += 1;
  }
  const [title, setTitle] = useState('');
  const [activityKind, setActivityKind] = useState<ActivityKind>('OTHER');
  const [recurrence, setRecurrence] = useState<Recurrence>('WEEKLY');
  const [occursOn, setOccursOn] = useState(defaultDate);
  const [weekdays, setWeekdays] = useState<Weekday[]>([weekdayForDate(defaultDate)]);
  const [startsAt, setStartsAt] = useState(defaultStartsAt);
  const [endsAt, setEndsAt] = useState(plusOneHour(defaultStartsAt));
  const [termId, setTermId] = useState(NO_VALUE);
  const [classroomId, setClassroomId] = useState(NO_VALUE);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
  const [classOfferingIds, setClassOfferingIds] = useState<string[]>([]);
  const [gradeLevelKeys, setGradeLevelKeys] = useState<string[]>([]);
  const [studentPersonIds, setStudentPersonIds] = useState<string[]>([]);
  const [allStudents, setAllStudents] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<SelectedStaff[]>([]);
  const [attendanceEnabled, setAttendanceEnabled] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [legacyClassParticipantWarning, setLegacyClassParticipantWarning] = useState(false);
  const [legacyClassOfferingIds, setLegacyClassOfferingIds] = useState<string[]>([]);
  const [scheduleEveryTerm, setScheduleEveryTerm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createBlock] = useCreateScheduleBlock();
  const [createClassSeries] = useCreateClassScheduleSeries();
  const [updateBlock] = useUpdateScheduleBlock();

  const sortedStaff = useMemo(
    () => [...staff].sort((a, b) => displayName(a.person).localeCompare(displayName(b.person))),
    [staff],
  );
  const sortedStudents = useMemo(
    () => [...students].sort((a, b) => displayName(a.person).localeCompare(displayName(b.person))),
    [students],
  );
  const sortedClasses = useMemo(
    () => [...availableClasses].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [availableClasses],
  );
  const selectedClass = availableClasses.find((item) => item.id === classOfferingIds[0]);
  const matchingTermClasses = useMemo(
    () => matchingClassTermOptions(availableClasses, selectedClass)
      .sort((first, second) => {
        const firstPosition = terms.findIndex((term) => term.id === first.term.id);
        const secondPosition = terms.findIndex((term) => term.id === second.term.id);
        return firstPosition - secondPosition || first.displayName.localeCompare(second.displayName);
      }),
    [availableClasses, selectedClass, terms],
  );
  const canScheduleEveryTerm = !block && activityKind === 'CLASS' && recurrence === 'WEEKLY' && matchingTermClasses.length > 1;
  const createsClassSeries = canScheduleEveryTerm && scheduleEveryTerm;
  const selectedTerm = terms.find((item) => item.id === termId) ??
    availableClasses.find((item) => item.term.id === termId)?.term;
  const occurrenceBounds = dateBounds(schoolYearStart, schoolYearEnd, selectedTerm);

  useEffect(() => {
    if (!open) {
      openSession.current = false;
      setLocationDialogOpen(false);
      setClassDialogOpen(false);
      setSaving(false);
      return;
    }
    if (openSession.current) return;
    openSession.current = true;

    const defaultClass = classes.find((item) => item.id === defaultClassOfferingId);
    const initialActivityKind = (block?.activityKind as ActivityKind) ?? (defaultClassOfferingId ? 'CLASS' : 'OTHER');
    const blockClassIds = block?.audiences
      .filter((item) => item.kind === 'CLASS')
      .flatMap((item) => item.classOffering ? [item.classOffering.id] : []) ?? [];
    const initialClassIds = blockClassIds.length > 0
      ? blockClassIds
      : (defaultClassOfferingId ? [defaultClassOfferingId] : []);
    const initialClass = block?.audiences
      .find((item) => item.kind === 'CLASS' && item.classOffering)?.classOffering ?? defaultClass;
    const hasLegacyClassParticipants = Boolean(
      block &&
      initialActivityKind === 'CLASS' &&
      (blockClassIds.length !== 1 || block.audiences.length !== 1 || block.staffAssignments.length > 0),
    );
    setLegacyClassParticipantWarning(hasLegacyClassParticipants);
    setLegacyClassOfferingIds(hasLegacyClassParticipants ? blockClassIds : []);
    setScheduleEveryTerm(Boolean(
      !block &&
      initialActivityKind === 'CLASS' &&
      matchingClassTermOptions(classes, initialClass as ClassOption | undefined).length > 1,
    ));
    setAvailableClasses([...classes]);
    setTitle(block?.title ?? '');
    setActivityKind(initialActivityKind);
    setRecurrence(block?.oneTime ? 'ONE_TIME' : 'WEEKLY');
    const initialTerm = initialActivityKind === 'CLASS' ? initialClass?.term : block?.term;
    const initialBounds = dateBounds(schoolYearStart, schoolYearEnd, initialTerm);
    const initialDate = clampDate(block?.oneTimeDate ?? defaultDate, initialBounds.startsOn, initialBounds.endsOn);
    setOccursOn(initialDate);
    setWeekdays(block?.weekdays.length ? block.weekdays.map((weekday) => weekday as Weekday) : [weekdayForDate(initialDate)]);
    setStartsAt(block?.startsAt.slice(0, 5) ?? defaultStartsAt);
    setEndsAt(block?.endsAt.slice(0, 5) ?? defaultEndsAt ?? plusOneHour(defaultStartsAt));
    setTermId(initialTerm?.id ?? NO_VALUE);
    setClassroomId(block?.classroom?.id ?? NO_VALUE);
    setClassOfferingIds(initialActivityKind === 'CLASS' ? initialClassIds.slice(0, 1) : initialClassIds);
    setGradeLevelKeys(block?.audiences.filter((item) => item.kind === 'GRADE_LEVEL').flatMap((item) => item.gradeLevelKey ? [item.gradeLevelKey] : []) ?? []);
    setStudentPersonIds(block?.audiences.filter((item) => item.kind === 'PERSON').flatMap((item) => item.person ? [item.person.id] : []) ?? []);
    setAllStudents(block?.audiences.some((item) => item.kind === 'ALL_STUDENTS') ?? false);
    setSelectedStaff(block?.staffAssignments.map((item) => ({
      staffMemberId: item.staffMember.id,
      roleLabel: item.roleLabel,
      canTakeAttendance: item.canTakeAttendance,
    })) ?? []);
    setAttendanceEnabled(block?.attendanceEnabled ?? false);
    setNotes(block?.notes ?? '');
    setError(null);
    setSaving(false);
    setLocationDialogOpen(false);
    setClassDialogOpen(false);
  }, [block, classes, defaultClassOfferingId, defaultDate, defaultEndsAt, defaultStartsAt, open, schoolYearEnd, schoolYearStart]);

  useEffect(() => {
    if (!open) return;
    setAvailableClasses((current) => {
      const byId = new Map(current.map((item) => [item.id, item]));
      classes.forEach((item) => byId.set(item.id, item));
      return [...byId.values()];
    });
  }, [classes, open]);

  const selectKind = (kind: ActivityKind) => {
    setActivityKind(kind);
    if (kind !== 'CLASS' && activityKind === 'CLASS' && legacyClassOfferingIds.length > 0) {
      setClassOfferingIds(legacyClassOfferingIds);
    }
    if (kind === 'CHAPEL') {
      setTitle((value) => value || 'Chapel');
      setAllStudents(true);
      setClassOfferingIds([]);
      setGradeLevelKeys([]);
      setStudentPersonIds([]);
      setAttendanceEnabled(true);
    } else if (kind === 'RECESS') {
      setTitle((value) => value || 'Recess');
      setAllStudents(false);
      setClassOfferingIds([]);
      setStudentPersonIds([]);
      setAttendanceEnabled(false);
    } else if (kind === 'CLASS') {
      setAllStudents(false);
      setClassOfferingIds((current) => current.slice(0, 1));
      const selectedClass = availableClasses.find((item) => item.id === classOfferingIds[0]);
      setScheduleEveryTerm(Boolean(
        !block && matchingClassTermOptions(availableClasses, selectedClass).length > 1,
      ));
      setTermId(selectedClass?.term.id ?? NO_VALUE);
      if (selectedClass) {
        const bounds = dateBounds(schoolYearStart, schoolYearEnd, selectedClass.term);
        setOccursOn((current) => clampDate(current, bounds.startsOn, bounds.endsOn));
      }
      setAttendanceEnabled(true);
    }
  };

  const toggleStaff = (staffMemberId: string, checked: boolean) => {
    setSelectedStaff((current) => checked
      ? [...current, { staffMemberId, roleLabel: 'Assigned', canTakeAttendance: false }]
      : current.filter((item) => item.staffMemberId !== staffMemberId));
  };

  const toggleClass = (item: ClassOption, checked: boolean) => {
    if (activityKind === 'CLASS') {
      setClassOfferingIds(checked ? [item.id] : []);
      setTermId(checked ? item.term.id : NO_VALUE);
      setScheduleEveryTerm(Boolean(
        checked && !block && matchingClassTermOptions(availableClasses, item).length > 1,
      ));
      if (checked) {
        const bounds = dateBounds(schoolYearStart, schoolYearEnd, item.term);
        setOccursOn((current) => clampDate(current, bounds.startsOn, bounds.endsOn));
      }
      return;
    }

    setClassOfferingIds((current) => toggleValue(current, item.id, checked));
    if (checked && termId === NO_VALUE) {
      const bounds = dateBounds(schoolYearStart, schoolYearEnd, item.term);
      setTermId(item.term.id);
      setOccursOn((current) => clampDate(current, bounds.startsOn, bounds.endsOn));
    }
  };

  const selectTerm = (value: string) => {
    const term = terms.find((item) => item.id === value) ?? availableClasses.find((item) => item.term.id === value)?.term;
    const bounds = dateBounds(schoolYearStart, schoolYearEnd, term);
    setTermId(value);
    setOccursOn((current) => clampDate(current, bounds.startsOn, bounds.endsOn));
  };

  const retainCreatedClass = (created: ClassOption) => {
    setAvailableClasses((current) => [...current.filter((item) => item.id !== created.id), created]);
    setClassOfferingIds([created.id]);
    const bounds = dateBounds(schoolYearStart, schoolYearEnd, created.term);
    setTermId(created.term.id);
    setScheduleEveryTerm(false);
    setOccursOn((current) => clampDate(current, bounds.startsOn, bounds.endsOn));
  };

  const updateStaff = (staffMemberId: string, change: Partial<SelectedStaff>) => {
    setSelectedStaff((current) => current.map((item) => item.staffMemberId === staffMemberId ? { ...item, ...change } : item));
  };

  const handleSave = async () => {
    const actionSession = sessionGeneration.current;
    setSaving(true);
    setError(null);

    const audiences = activityKind === 'CLASS'
      ? classOfferingIds.slice(0, 1).map((classOfferingId) => ({ kind: 'CLASS' as const, classOfferingId }))
      : [
        ...classOfferingIds.map((classOfferingId) => ({ kind: 'CLASS' as const, classOfferingId })),
        ...gradeLevelKeys.map((gradeLevelKey) => ({ kind: 'GRADE_LEVEL' as const, gradeLevelKey })),
        ...(allStudents ? [{ kind: 'ALL_STUDENTS' as const }] : []),
        ...studentPersonIds.map((personId) => ({ kind: 'PERSON' as const, personId })),
      ];
    const attributes = {
      title: title.trim() || null,
      activityKind,
      termId: activityKind === 'CLASS' ? selectedClass?.term.id ?? null : termId === NO_VALUE ? null : termId,
      startsAt,
      endsAt,
      weekdays: recurrence === 'WEEKLY' ? weekdays : [],
      occursOn: recurrence === 'ONE_TIME' ? occursOn : null,
      classroomId: classroomId === NO_VALUE ? null : classroomId,
      attendanceEnabled,
      notes: notes.trim() || null,
      audiences,
      staffMembers: activityKind === 'CLASS' ? [] : selectedStaff,
    };

    let errors: ReadonlyArray<{ message: string }>;
    try {
      if (block) {
        errors = (await updateBlock({ variables: { input: { id: block.id, attributes } } }))
          .data?.updateScheduleBlock?.userErrors ?? [];
      } else if (createsClassSeries && selectedClass) {
        const seriesAttributes = {
          title: attributes.title,
          activityKind: attributes.activityKind,
          startsAt: attributes.startsAt,
          endsAt: attributes.endsAt,
          weekdays: attributes.weekdays,
          occursOn: attributes.occursOn,
          classroomId: attributes.classroomId,
          attendanceEnabled: attributes.attendanceEnabled,
          notes: attributes.notes,
        };
        errors = (await createClassSeries({
          variables: {
            input: {
              schoolYearId,
              classOfferingId: selectedClass.id,
              attributes: seriesAttributes,
            },
          },
        })).data?.createClassScheduleSeries?.userErrors ?? [];
      } else {
        errors = (await createBlock({ variables: { input: { schoolYearId, attributes } } }))
          .data?.createScheduleBlock?.userErrors ?? [];
      }
    } catch (caught) {
      if (sessionGeneration.current !== actionSession) return;
      setError(caught instanceof Error ? caught.message : 'Unable to save this schedule block.');
      setSaving(false);
      return;
    }

    if (sessionGeneration.current !== actionSession) return;
    if (errors.length > 0) {
      setError(errors.map((item) => item.message).join(', '));
      setSaving(false);
      return;
    }

    // The mutation is committed. Close before refreshing so a refresh failure
    // cannot invite a duplicate create/update retry from this dialog.
    onOpenChange(false);
    try {
      await onComplete();
    } catch (refreshError) {
      const detail = refreshError instanceof Error && refreshError.message ? `: ${refreshError.message}` : '';
      const savedLabel = createsClassSeries ? 'Class schedule saved' : 'Schedule block saved';
      onRefreshError?.(`${savedLabel}, but the schedule could not be refreshed${detail}. Reload the page to see the saved changes.`);
    } finally {
      if (sessionGeneration.current === actionSession) setSaving(false);
    }
  };

  const canSave = Boolean(
    startsAt && endsAt && startsAt < endsAt &&
    (recurrence === 'ONE_TIME'
      ? occursOn && occursOn >= occurrenceBounds.startsOn && occursOn <= occurrenceBounds.endsOn
      : weekdays.length > 0) &&
    (activityKind === 'CLASS' ? classOfferingIds.length === 1 : title.trim()),
  );
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && saving) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{block ? 'Edit schedule block' : 'Add schedule block'}</DialogTitle>
          <DialogDescription>
            One block owns its pattern, location, audience, and scheduled staff. Overlaps are allowed.
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-5 py-2" disabled={saving}>
          {error && <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
          {legacyClassParticipantWarning && activityKind === 'CLASS' && (
            <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
              <span className="font-medium">Legacy class setup:</span>{' '}
              this block has manual or additional audiences or scheduled staff. Saving will replace them with the selected class roster and instructors.
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="schedule-title">Title {activityKind === 'CLASS' && '(optional)'}</Label>
              <Input id="schedule-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder={activityKind === 'CLASS' ? 'Uses the class name by default' : 'Chapel'} />
            </div>
            <div className="space-y-2">
              <Label>Activity type</Label>
              <Select value={activityKind} onValueChange={(value) => selectKind(value as ActivityKind)}>
                <SelectTrigger aria-label="Activity type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_KINDS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => selectKind('CHAPEL')}>Chapel preset</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => selectKind('RECESS')}>Recess preset</Button>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Pattern</Label>
              <Select value={recurrence} onValueChange={(value) => setRecurrence(value as Recurrence)}>
                <SelectTrigger aria-label="Pattern"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WEEKLY">Weekly pattern</SelectItem>
                  <SelectItem value="ONE_TIME">One-time event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {activityKind === 'CLASS' ? (
              <div className="space-y-2">
                <Label>{createsClassSeries ? 'Terms' : 'Term'}</Label>
                <p className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm">
                  {createsClassSeries
                    ? `Terms: ${matchingTermClasses.map((item) => item.term.name).join(', ')}`
                    : selectedTerm ? `Term: ${selectedTerm.name}` : 'Select a class to set its term'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Term (optional)</Label>
                <Select value={termId} onValueChange={selectTerm}>
                  <SelectTrigger aria-label="Term"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_VALUE}>Whole school year</SelectItem>
                    {terms.map((term) => <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {recurrence === 'ONE_TIME' ? (
            <div className="space-y-2">
              <Label htmlFor="schedule-date">Date</Label>
              <Input id="schedule-date" type="date" min={occurrenceBounds.startsOn} max={occurrenceBounds.endsOn} value={occursOn} onChange={(event) => {
                const nextDate = event.target.value;
                setOccursOn(nextDate ? clampDate(nextDate, occurrenceBounds.startsOn, occurrenceBounds.endsOn) : nextDate);
              }} />
            </div>
          ) : (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Weekdays</legend>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((day) => (
                  <Button
                    key={day}
                    type="button"
                    size="sm"
                    variant={weekdays.includes(day) ? 'default' : 'outline'}
                    aria-pressed={weekdays.includes(day)}
                    onClick={() => setWeekdays((current) => toggleValue(current, day, !current.includes(day)))}
                  >
                    {WEEKDAY_LABELS[day].slice(0, 3)}
                  </Button>
                ))}
              </div>
            </fieldset>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="schedule-start">Starts</Label><Input id="schedule-start" type="time" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} /></div>
            <div className="space-y-2"><Label htmlFor="schedule-end">Ends</Label><Input id="schedule-end" type="time" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} /></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Location</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => setLocationDialogOpen(true)}>Add location</Button>
            </div>
            <Select value={classroomId} onValueChange={setClassroomId}>
              <SelectTrigger><SelectValue placeholder="No location assigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_VALUE}>No location assigned</SelectItem>
                {classrooms.map((classroom) => <SelectItem key={classroom.id} value={classroom.id}>{classroom.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <fieldset className="rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">{activityKind === 'CLASS' ? 'Class' : 'Audience'}</legend>
            {activityKind !== 'CLASS' && (
              <label className="mb-3 flex items-center gap-2 text-sm">
                <Checkbox checked={allStudents} onCheckedChange={(value) => setAllStudents(value === true)} /> All eligible students
              </label>
            )}
            <div className={activityKind === 'CLASS' ? '' : 'grid gap-4 lg:grid-cols-3'}>
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Classes</h4>
                  {activityKind === 'CLASS' && allowInlineClassCreation && (
                    <Button type="button" variant="ghost" size="sm" disabled={terms.length === 0} onClick={() => setClassDialogOpen(true)}>Create class</Button>
                  )}
                </div>
                {activityKind === 'CLASS' && allowInlineClassCreation && terms.length === 0 && (
                  <div role="alert" className="mb-2 rounded-md bg-muted p-2 text-xs text-muted-foreground">
                    <p>Configure at least one academic term before creating or scheduling a class.</p>
                    {configureTermsHref && (
                      <a className="mt-1 inline-block font-medium text-primary underline" href={configureTermsHref}>
                        Open School Years &amp; Terms
                      </a>
                    )}
                  </div>
                )}
                <div className="max-h-44 space-y-2 overflow-y-auto">
                  {sortedClasses.map((item) => <Choice key={item.id} id={`class-${item.id}`} checked={classOfferingIds.includes(item.id)} onChange={(checked) => toggleClass(item, checked)} label={item.displayName} detail={item.term.name} />)}
                  {sortedClasses.length === 0 && <p className="text-xs text-muted-foreground">No classes yet.</p>}
                </div>
                {canScheduleEveryTerm && (
                  <div className="mt-3 rounded-md border bg-muted/30 p-3">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Checkbox
                        aria-label="Schedule in every term"
                        checked={scheduleEveryTerm}
                        onCheckedChange={(value) => setScheduleEveryTerm(value === true)}
                      />
                      Schedule in every term
                    </label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Creates {matchingTermClasses.length} separate blocks covering {matchingTermClasses.map((item) => item.term.name).join(', ')}. Each uses that term's class roster and instructors.
                    </p>
                  </div>
                )}
              </div>
              {activityKind !== 'CLASS' && (
                <>
                  <ChoiceList title="Grade levels">
                    {gradeLevels.map((grade) => <Choice key={grade} id={`grade-${grade}`} checked={gradeLevelKeys.includes(grade)} onChange={(checked) => setGradeLevelKeys((current) => toggleValue(current, grade, checked))} label={grade} />)}
                  </ChoiceList>
                  <ChoiceList title="Individual students">
                    {sortedStudents.map((item) => <Choice key={item.person.id} id={`student-${item.person.id}`} checked={studentPersonIds.includes(item.person.id)} onChange={(checked) => setStudentPersonIds((current) => toggleValue(current, item.person.id, checked))} label={displayName(item.person)} detail={item.gradeLevel ?? undefined} />)}
                  </ChoiceList>
                </>
              )}
            </div>
          </fieldset>

          {activityKind !== 'CLASS' && <fieldset className="rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">Scheduled staff</legend>
            <div className="space-y-3">
              {sortedStaff.map((item) => {
                const selection = selectedStaff.find((selected) => selected.staffMemberId === item.id);
                return (
                  <div key={item.id} className="rounded-md border p-2">
                    <Choice id={`staff-${item.id}`} checked={!!selection} onChange={(checked) => toggleStaff(item.id, checked)} label={displayName(item.person)} detail={item.kind.toLowerCase()} />
                    {selection && (
                      <div className="ml-6 mt-2 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                        <Input aria-label={`Role for ${displayName(item.person)}`} value={selection.roleLabel} onChange={(event) => updateStaff(item.id, { roleLabel: event.target.value })} placeholder="Role on this block" />
                        <label className="flex items-center gap-2 text-xs"><Checkbox checked={selection.canTakeAttendance} onCheckedChange={(value) => updateStaff(item.id, { canTakeAttendance: value === true })} /> Can take attendance</label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>}

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={attendanceEnabled} onCheckedChange={(value) => setAttendanceEnabled(value === true)} /> Attendance may be taken for this block
          </label>

          <div className="space-y-2"><Label htmlFor="schedule-notes">Notes</Label><Textarea id="schedule-notes" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
        </fieldset>

        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !canSave}>
            {saving ? 'Saving…' : createsClassSeries ? `Save ${matchingTermClasses.length} term blocks` : 'Save block'}
          </Button>
        </DialogFooter>
        <ClassroomDialog
          open={locationDialogOpen}
          onOpenChange={setLocationDialogOpen}
          schoolId={schoolId}
          onComplete={async () => { await onLocationCreated?.(); }}
          onRefreshError={onRefreshError}
        />
        {allowInlineClassCreation && classDialogOpen && (
          <ClassOfferingDialog
            open
            onOpenChange={setClassDialogOpen}
            schoolYearId={schoolYearId}
            courses={courses}
            terms={terms}
            configureTermsHref={configureTermsHref}
            onCreated={retainCreatedClass}
            onOptionsRefresh={onClassOptionsRefresh}
            onRefreshError={onRefreshError}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChoiceList({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{title}</h4><div className="max-h-44 space-y-2 overflow-y-auto">{children}</div></div>;
}

function Choice({ id, checked, onChange, label, detail }: { id: string; checked: boolean; onChange: (checked: boolean) => void; label: string; detail?: string }) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      <Label htmlFor={id} className="min-w-0 cursor-pointer font-normal leading-tight"><span className="block truncate">{label}</span>{detail && <span className="block text-xs text-muted-foreground">{detail}</span>}</Label>
    </div>
  );
}
